import _ from 'lodash';
import { Observable } from 'rxjs';
import { startSubmit, stopSubmit, initialize } from 'redux-form/immutable';
import { replace, push } from 'react-router-redux';

import BudgetApi from 'entities/budget/api';
import { selectBudgets } from 'entities/budget/selectors';
import BudgetAllocationApi from 'entities/budgetAllocation/api';
import BudgetAllocation from 'entities/budgetAllocation/model';
import { selectBudgetAllocationById } from 'entities/budgetAllocation/selectors';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { selectUserLocations } from 'entities/user/selectors';
import LocationsApi from 'entities/location/api';

import { FORM_IDENTIFIER, INIT, SAVE, REMOVE } from './constants';
import {
  initSuccessAction,
  initFailedAction,
  saveSuccessAction,
  saveFailedAction,
  removeSuccessAction,
  removeFailedAction,
} from './actions';

const init = (action$, store) =>
  action$.ofType(INIT).mergeMap(({ id, budgetId }) => {
    const state = store.getState();
    const actions: Observable<any>[] = [BudgetApi.fetchAll()];

    const locations = selectUserLocations(state);
    if (locations.size === 0) {
      actions.push(LocationsApi.fetchAll());
    }

    if (id === 'new') {
      const budgets = selectBudgets(state);
      const budget = budgets.get(budgetId);

      actions.push(
        Observable.of(
          initialize(
            FORM_IDENTIFIER,
            new BudgetAllocation({
              budget: budgetId,
              currencyCode: budget?.get('currencyCode') || '',
            }).toForm()
          )
        )
      );
    } else {
      const budgetAllocation = selectBudgetAllocationById(state, id);
      if (budgetAllocation) {
        actions.push(Observable.of(initialize(FORM_IDENTIFIER, budgetAllocation.toForm())));
      } else {
        actions.push(
          BudgetAllocationApi.fetch(id)
            .map((action) => [
              action,
              initialize(FORM_IDENTIFIER, action.budgetAllocation?.toForm()),
            ])
            .catch((e) => Observable.of(initFailedAction(e), replace('/notFound')))
        );
      }
    }

    return Observable.combineLatest(actions)
      .mergeMap((resultingActions) => [..._.flatten(resultingActions), initSuccessAction()])
      .catch((e) => Observable.of(initFailedAction(e)));
  });

const save = (action$) =>
  action$.ofType(SAVE).mergeMap((payload) =>
    Observable.concat(
      Observable.of(startSubmit(FORM_IDENTIFIER)),
      Observable.from(
        payload.data.id
          ? BudgetAllocationApi.update(payload.data.id, payload.data, payload.budgetType)
          : BudgetAllocationApi.create(payload.data, payload.budgetType)
      )
        .mergeMap((action) => [
          action,
          saveSuccessAction(FORM_IDENTIFIER),
          stopSubmit(FORM_IDENTIFIER),
          initialize(FORM_IDENTIFIER, action.budgetAllocation.toJS()),
          push('/donations/rules'),
          showMessageAction(payload.data.id ? 'Changes Saved!' : 'Rule Created!'),
        ])
        .catch((e) =>
          Observable.of(
            saveFailedAction(e, 'saveFailed'),
            stopSubmit(FORM_IDENTIFIER),
            showErrorAction('An error occurred during saving, please try again.')
          )
        )
    )
  );

const remove = (action$) =>
  action$.ofType(REMOVE).mergeMap((payload) =>
    Observable.from(BudgetAllocationApi.delete(payload.budgetAllocationId, payload.budgetType))
      .mergeMap((action) => [
        action,
        removeSuccessAction(),
        push('/donations/rules'),
        showMessageAction('Donation Credit Rule Removed!'),
      ])
      .catch((e) =>
        Observable.of(
          removeFailedAction(e, 'removeFailed'),
          stopSubmit(FORM_IDENTIFIER),
          showErrorAction('An error occurred during saving, please try again.')
        )
      )
  );

export default [init, save, remove];
